import { useEffect, useState } from 'react';
import testimonialsData from '../data/testimonials.json';
import { TestimonialApi } from '../api/adminApi';

const normalizeTestimonial = (testimonial = {}, index = 0) => ({
  ...testimonial,
  sortOrder: typeof testimonial.sortOrder === 'number' ? testimonial.sortOrder : index
});

const sortTestimonials = (list = []) =>
  [...list].sort((a, b) => {
    const orderDiff = (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
    if (orderDiff !== 0) {
      return orderDiff;
    }
    return (a.id ?? 0) - (b.id ?? 0);
  });

const prepareTestimonials = (list = []) => sortTestimonials(list.map((testimonial, index) => normalizeTestimonial(testimonial, index)));

const fallbackTestimonials = prepareTestimonials(testimonialsData.testimonials);

const cache = new Map();
const pendingRequests = new Map();

const normalizePath = (inputPath) => {
  if (!inputPath) {
    return '';
  }
  let path = inputPath.trim();
  if (!path) {
    return '';
  }
  if (!path.startsWith('/')) {
    path = `/${path}`;
  }
  path = path.replace(/\/+/g, '/');
  path = path.replace(/\/+$/, '');
  if (!path) {
    return '/';
  }
  return path === '/' ? '/' : path.toLowerCase();
};

const buildKey = (pagePath) => {
  const normalized = normalizePath(pagePath);
  return normalized || 'all';
};

const loadTestimonials = async (pagePath) => {
  const key = buildKey(pagePath);
  if (cache.has(key)) {
    return cache.get(key);
  }
  if (pendingRequests.has(key)) {
    return pendingRequests.get(key);
  }

  const isAll = key === 'all';
  const request = TestimonialApi.getAll(isAll ? undefined : normalizePath(pagePath))
    .then((remoteTestimonials) => {
      const hasRemote = Array.isArray(remoteTestimonials) && remoteTestimonials.length;
      const normalized = hasRemote ? prepareTestimonials(remoteTestimonials) : isAll ? fallbackTestimonials : [];
      cache.set(key, normalized);
      return normalized;
    })
    .catch((error) => {
      pendingRequests.delete(key);
      throw error;
    })
    .finally(() => {
      pendingRequests.delete(key);
    });

  pendingRequests.set(key, request);
  return request;
};

export const useTestimonials = (pagePath) => {
  const normalizedPath = normalizePath(pagePath);
  const cacheKey = buildKey(normalizedPath);
  const initialValue = cache.has(cacheKey) ? cache.get(cacheKey) : fallbackTestimonials;
  const hasCache = cache.has(cacheKey);
  const [testimonials, setTestimonials] = useState(initialValue);
  const [isLoading, setIsLoading] = useState(!hasCache);
  const [error, setError] = useState('');
  const [isFallback, setIsFallback] = useState(!hasCache);

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setError('');

    loadTestimonials(normalizedPath)
      .then((data) => {
        if (!isMounted) return;
        if (Array.isArray(data) && data.length) {
          setTestimonials(data);
          setIsFallback(false);
        } else if (normalizedPath) {
          setTestimonials([]);
          setIsFallback(false);
        } else {
          setTestimonials(fallbackTestimonials);
          setIsFallback(true);
        }
      })
      .catch(() => {
        if (!isMounted) return;
        setError('Unable to load testimonials right now. Showing defaults.');
        setTestimonials(fallbackTestimonials);
        setIsFallback(true);
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [normalizedPath]);

  return { testimonials, isLoading, error, isFallback };
};
